import { useCallback, useEffect, useRef, useState } from 'react'
import { searchPlaces } from '../lib/placesSearch'

// Input with a floating suggestion list wired to searchPlaces().
// `kind` picks the pool: 'hotel' → hotels only, 'address' → any place.
// `onPick(place)` fires when the user commits a suggestion.
//
// The input stays uncontrolled from the outside — the parent owns the
// visible value via `value` and mirror-writes on change. That keeps the
// same field usable both as an autocomplete AND as a plain text input
// (e.g. when the user types a hotel we don't have on file).
export default function AddressAutocomplete({
  id,
  value,
  onChange,
  onPick,
  kind = 'address',
  placeholder = '',
  autoComplete = 'off',
  className = 'field-input',
  required = false,
  maxLength,
}) {
  const [hits, setHits] = useState([])
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const wrapRef = useRef(null)
  const inputRef = useRef(null)
  const seqRef = useRef(0) // guards stale async results

  const debounceRef = useRef(null)
  const runSearch = useCallback((q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    const trimmed = String(q || '').trim()
    if (trimmed.length < 2) {
      seqRef.current++
      setHits([]); setOpen(false); return
    }
    debounceRef.current = setTimeout(async () => {
      const seq = ++seqRef.current
      let results = []
      try {
        results = await searchPlaces(q, { kind })
      } catch { /* fall through to empty */ }
      if (seq !== seqRef.current) return
      setHits(results)
      setOpen(results.length > 0)
      setActive(results.length > 0 ? 0 : -1)
    }, 220)
  }, [kind])

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current) }, [])

  useEffect(() => {
    if (!open) return
    const onDown = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  function handleChange(e) {
    const v = e.target.value
    onChange?.(v)
    runSearch(v)
  }

  function handleFocus() {
    if (hits.length > 0) setOpen(true)
  }

  function commit(place) {
    onPick?.(place)
    setOpen(false)
    setHits([])
    setActive(-1)
    inputRef.current?.blur()
  }

  function handleKey(e) {
    if (!open || hits.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((a) => Math.min(hits.length - 1, a + 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((a) => Math.max(0, a - 1))
    } else if (e.key === 'Enter') {
      if (active >= 0 && active < hits.length) {
        e.preventDefault()
        commit(hits[active])
      }
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  return (
    <div ref={wrapRef} className="relative">
      <input
        ref={inputRef}
        id={id}
        type="text"
        value={value || ''}
        onChange={handleChange}
        onFocus={handleFocus}
        onKeyDown={handleKey}
        placeholder={placeholder}
        autoComplete={autoComplete}
        required={required}
        maxLength={maxLength}
        className={className}
      />
      {open && hits.length > 0 && (
        <ul
          role="listbox"
          className="absolute left-0 right-0 top-full z-40 mt-1 max-h-[260px] overflow-y-auto rounded-lg border border-line bg-white shadow-lg"
        >
          {hits.map((h, i) => (
            <li
              key={h.id}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => { e.preventDefault(); commit(h) }}
              onMouseEnter={() => setActive(i)}
              className={`flex cursor-pointer items-start gap-2 px-3 py-2 text-[12px] ${i === active ? 'bg-navy/5' : ''}`}
            >
              <span aria-hidden className="text-[14px] leading-tight">{h.isHotel ? '🏨' : '📍'}</span>
              <div className="min-w-0 flex-1">
                <div className="truncate font-bold text-navy">
                  {kind === 'hotel' ? h.hotel : h.street}
                </div>
                <div className="truncate text-[11px] text-ink-2">
                  {kind === 'hotel'
                    ? `${h.street} · ${h.city}, ${h.state} ${h.zip}`
                    : h.hotel
                    ? `${h.hotel} · ${h.city}, ${h.state} ${h.zip}`
                    : `${h.city}, ${h.state} ${h.zip}`}
                </div>
              </div>
            </li>
          ))}
          <li className="border-t border-line px-3 py-1.5 text-[10px] italic text-ink-3">
            Suggestions — powered by the maps service
          </li>
        </ul>
      )}
    </div>
  )
}
